// =============================================
// HelpGuideModal — מדריך עזרה מלא
// Ctrl+/ לפתיחה, חיפוש, PDF, צילומי מסך
// =============================================

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import {
  X, Search, FileDown, ChevronLeft, ChevronRight,
  LogIn, UserPlus, KeyRound, Landmark, UserCog, Users,
  Building2, Package, FolderTree, CalendarDays, Settings,
  Wallet, Receipt, ScanLine, ShoppingCart, Contact, Headphones,
  Menu,
} from 'lucide-react';
import { guideSections, getGuideSectionsForRole, findSectionByRoute } from '../../constants/guideContent';
import type { GuideSection, UserRole } from '../../constants/guideContent';

interface HelpGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  userRole: UserRole | null;
}

// מיפוי אייקון
const ICON_MAP: Record<string, LucideIcon> = {
  LogIn, UserPlus, KeyRound, Landmark, UserCog, Users,
  Building2, Package, FolderTree, CalendarDays, Settings,
  Wallet, Receipt, ScanLine, ShoppingCart, Contact, Headphones,
};

// הדגשת טקסט חיפוש
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} style={{ backgroundColor: '#fef08a', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
      : part
  );
}

export default function HelpGuideModal({ isOpen, onClose, currentPath, userRole }: HelpGuideModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // סקציות מסוננות לפי תפקיד
  const sections = useMemo(() => {
    if (!userRole) return guideSections;
    return getGuideSectionsForRole(userRole);
  }, [userRole]);

  // סינון לפי חיפוש
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.toLowerCase();
    return sections.filter(s =>
      s.title.toLowerCase().includes(q) ||
      s.introduction.toLowerCase().includes(q) ||
      s.steps.some(step => step.toLowerCase().includes(q)) ||
      s.fields.some(f => f.name.toLowerCase().includes(q) || f.tooltip.toLowerCase().includes(q))
    );
  }, [sections, searchQuery]);

  // סקציה נבחרת
  const selectedSection = useMemo(() => {
    if (selectedId) return filteredSections.find(s => s.id === selectedId) || filteredSections[0];
    return filteredSections[0];
  }, [selectedId, filteredSections]);

  // בחירה אוטומטית לפי route בפתיחה
  useEffect(() => {
    if (isOpen && currentPath) {
      const match = findSectionByRoute(currentPath);
      if (match && sections.some(s => s.id === match.id)) {
        setSelectedId(match.id);
      } else if (sections.length > 0) {
        setSelectedId(sections[0].id);
      }
    }
  }, [isOpen, currentPath, sections]);

  // Escape סוגר
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // גלילה למעלה כשמחליפים סקציה
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [selectedId]);

  // ייצוא PDF
  const handleExportPDF = useCallback(async () => {
    if (!printRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const html2pdfModule = await import('html2pdf.js');
      const html2pdf = html2pdfModule.default;
      await html2pdf().set({
        margin: [10, 10, 10, 10] as [number, number, number, number],
        filename: 'kipikid-guide.pdf',
        image: { type: 'jpeg' as const, quality: 0.85 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
      }).from(printRef.current).save();
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting]);

  if (!isOpen) return null;

  const SectionIcon = selectedSection ? ICON_MAP[selectedSection.icon] : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            dir="rtl"
            style={{
              width: '95vw',
              maxWidth: '1200px',
              height: '90vh',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0,
              backgroundColor: '#fafafa',
            }}>
              {/* Mobile sidebar toggle */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  padding: '8px', border: 'none', backgroundColor: 'transparent',
                  cursor: 'pointer', borderRadius: '8px',
                  display: 'none', // shown via media query
                }}
                className="help-guide-mobile-menu"
              >
                <Menu size={20} color="#6b7280" />
              </button>

              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={18} color="#9ca3af" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="חיפוש במדריך..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 40px 10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: 'white',
                  }}
                />
              </div>

              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '10px 16px', borderRadius: '10px',
                  backgroundColor: isExporting ? '#d1d5db' : '#0891b2',
                  color: 'white', border: 'none', cursor: isExporting ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap',
                }}
              >
                <FileDown size={16} />
                {isExporting ? 'מייצא...' : 'PDF'}
              </button>

              <button
                onClick={onClose}
                style={{
                  padding: '8px', border: 'none', backgroundColor: 'transparent',
                  cursor: 'pointer', borderRadius: '8px',
                }}
              >
                <X size={22} color="#6b7280" />
              </button>
            </div>

            {/* Body: Sidebar + Content */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
              {/* Sidebar */}
              <nav
                className="help-guide-sidebar"
                style={{
                  width: '260px',
                  borderLeft: '1px solid #e5e7eb',
                  overflowY: 'auto',
                  padding: '12px 8px',
                  flexShrink: 0,
                  backgroundColor: '#fafafa',
                }}
              >
                {filteredSections.length === 0 ? (
                  <p style={{ padding: '16px', color: '#9ca3af', fontSize: '14px', textAlign: 'center' }}>
                    לא נמצאו תוצאות
                  </p>
                ) : (
                  filteredSections.map(section => {
                    const Icon = ICON_MAP[section.icon];
                    const isActive = selectedSection?.id === section.id;
                    return (
                      <button
                        key={section.id}
                        onClick={() => { setSelectedId(section.id); setSidebarOpen(false); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '10px 14px',
                          borderRadius: '10px',
                          border: 'none',
                          backgroundColor: isActive ? '#e0f2fe' : 'transparent',
                          color: isActive ? '#0369a1' : '#374151',
                          fontWeight: isActive ? 600 : 400,
                          fontSize: '13px',
                          cursor: 'pointer',
                          textAlign: 'right',
                          marginBottom: '2px',
                          transition: 'background-color 0.15s',
                        }}
                      >
                        {Icon && <Icon size={18} color={isActive ? '#0369a1' : '#6b7280'} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {highlightText(section.title, searchQuery)}
                        </span>
                      </button>
                    );
                  })
                )}
              </nav>

              {/* Mobile sidebar overlay */}
              {sidebarOpen && (
                <div
                  className="help-guide-sidebar-overlay"
                  onMouseDown={() => setSidebarOpen(false)}
                  style={{
                    position: 'absolute', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 5,
                  }}
                />
              )}

              {/* Mobile sidebar panel */}
              {sidebarOpen && (
                <nav
                  className="help-guide-sidebar-mobile"
                  style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0,
                    width: '260px', backgroundColor: '#fafafa',
                    borderLeft: '1px solid #e5e7eb', zIndex: 10,
                    overflowY: 'auto', padding: '12px 8px',
                    boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
                  }}
                >
                  {filteredSections.map(section => {
                    const Icon = ICON_MAP[section.icon];
                    const isActive = selectedSection?.id === section.id;
                    return (
                      <button
                        key={section.id}
                        onClick={() => { setSelectedId(section.id); setSidebarOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          width: '100%', padding: '10px 14px', borderRadius: '10px',
                          border: 'none', cursor: 'pointer', textAlign: 'right',
                          backgroundColor: isActive ? '#e0f2fe' : 'transparent',
                          color: isActive ? '#0369a1' : '#374151',
                          fontWeight: isActive ? 600 : 400, fontSize: '13px',
                          marginBottom: '2px',
                        }}
                      >
                        {Icon && <Icon size={18} color={isActive ? '#0369a1' : '#6b7280'} />}
                        <span>{section.title}</span>
                      </button>
                    );
                  })}
                </nav>
              )}

              {/* Main Content */}
              <div
                ref={contentRef}
                style={{
                  flex: 1, overflowY: 'auto', padding: '32px 40px',
                }}
              >
                {selectedSection ? (
                  <SectionContent section={selectedSection} searchQuery={searchQuery} />
                ) : (
                  <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '80px' }}>
                    <p style={{ fontSize: '16px' }}>בחר נושא מהתפריט</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer navigation */}
            {selectedSection && filteredSections.length > 1 && (
              <div style={{
                padding: '12px 24px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fafafa',
                flexShrink: 0,
              }}>
                <NavigationButton
                  sections={filteredSections}
                  currentId={selectedSection.id}
                  direction="next"
                  onNavigate={setSelectedId}
                />
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {filteredSections.findIndex(s => s.id === selectedSection.id) + 1} / {filteredSections.length}
                </span>
                <NavigationButton
                  sections={filteredSections}
                  currentId={selectedSection.id}
                  direction="prev"
                  onNavigate={setSelectedId}
                />
              </div>
            )}
          </motion.div>

          {/* Hidden print div for PDF */}
          <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '800px' }}>
            <div ref={printRef} dir="rtl" style={{ fontFamily: 'Arial, sans-serif', padding: '20px', backgroundColor: 'white' }}>
              <h1 style={{ fontSize: '24px', textAlign: 'center', marginBottom: '8px', color: '#0891b2' }}>
                מדריך למשתמש — KipiKid
              </h1>
              <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '12px', marginBottom: '32px' }}>
                מערכת ניהול נוכחות לגני ילדים
              </p>
              {sections.map(section => (
                <div key={section.id} style={{ marginBottom: '28px', pageBreakInside: 'avoid' }}>
                  <h2 style={{
                    fontSize: '18px', color: '#0369a1', borderBottom: '2px solid #e0f2fe',
                    paddingBottom: '6px', marginBottom: '12px',
                  }}>
                    {section.title}
                  </h2>

                  {section.image && (
                    <img
                      src={section.image}
                      alt={section.title}
                      style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', marginBottom: '12px' }}
                    />
                  )}

                  <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '12px' }}>
                    {section.introduction}
                  </p>

                  <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#1f2937' }}>שלבי ביצוע:</h3>
                  <ol style={{ paddingRight: '20px', fontSize: '13px', color: '#374151', lineHeight: 1.8 }}>
                    {section.steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>

                  {section.tips && section.tips.length > 0 && (
                    <>
                      <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '10px', marginBottom: '4px', color: '#1f2937' }}>טיפים:</h3>
                      <ul style={{ paddingRight: '20px', fontSize: '13px', color: '#6b7280', lineHeight: 1.8 }}>
                        {section.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                      </ul>
                    </>
                  )}

                  {section.fields.length > 0 && (
                    <>
                      <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '10px', marginBottom: '6px', color: '#1f2937' }}>שדות:</h3>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f1f5f9' }}>
                            <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>שדה</th>
                            <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>תיאור</th>
                            <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>עזרה</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.fields.map((f, i) => (
                            <tr key={i}>
                              <td style={{ padding: '5px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 500 }}>{f.name}</td>
                              <td style={{ padding: '5px 10px', borderBottom: '1px solid #f1f5f9' }}>{f.tooltip}</td>
                              <td style={{ padding: '5px 10px', borderBottom: '1px solid #f1f5f9', color: '#6b7280' }}>{f.helper || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              ))}
              <p style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', marginTop: '32px' }}>
                KipiKid &copy; {new Date().getFullYear()} — מדריך זה נוצר אוטומטית
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .help-guide-sidebar {
            display: none !important;
          }
          .help-guide-mobile-menu {
            display: flex !important;
          }
        }
        @media (min-width: 769px) {
          .help-guide-sidebar-overlay,
          .help-guide-sidebar-mobile {
            display: none !important;
          }
        }
      `}</style>
    </AnimatePresence>
  );
}

// ===== Section Content Component =====
function SectionContent({ section, searchQuery }: { section: GuideSection; searchQuery: string }) {
  const SectionIcon = ICON_MAP[section.icon];

  return (
    <div>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        {SectionIcon && (
          <div style={{
            width: '44px', height: '44px', borderRadius: '12px',
            backgroundColor: '#e0f2fe', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <SectionIcon size={22} color="#0369a1" />
          </div>
        )}
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1f2937' }}>
          {highlightText(section.title, searchQuery)}
        </h2>
      </div>

      {/* Screenshot */}
      {section.image && (
        <img
          src={section.image}
          alt={section.title}
          loading="lazy"
          style={{
            width: '100%',
            maxHeight: '360px',
            objectFit: 'contain',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            backgroundColor: '#f9fafb',
          }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}

      {/* Introduction */}
      <p style={{
        fontSize: '15px', color: '#374151', lineHeight: 1.8,
        marginBottom: '24px', backgroundColor: '#f8fafc',
        padding: '16px 20px', borderRadius: '12px',
        borderRight: '4px solid #0891b2',
      }}>
        {highlightText(section.introduction, searchQuery)}
      </p>

      {/* Steps */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{
          fontSize: '16px', fontWeight: 600, color: '#1f2937',
          marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          שלבי ביצוע
        </h3>
        <ol style={{
          paddingRight: '0', margin: 0, listStyle: 'none',
          display: 'flex', flexDirection: 'column', gap: '8px',
        }}>
          {section.steps.map((step, i) => (
            <li key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px',
              padding: '10px 14px', backgroundColor: '#f9fafb',
              borderRadius: '10px', fontSize: '14px', color: '#374151',
              lineHeight: 1.6,
            }}>
              <span style={{
                minWidth: '28px', height: '28px', borderRadius: '50%',
                backgroundColor: '#0891b2', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 600, flexShrink: 0,
              }}>
                {i + 1}
              </span>
              <span style={{ paddingTop: '3px' }}>{highlightText(step, searchQuery)}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Tips */}
      {section.tips && section.tips.length > 0 && (
        <div style={{
          marginBottom: '24px', backgroundColor: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: '12px',
          padding: '16px 20px',
        }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>
            💡 טיפים
          </h3>
          <ul style={{ paddingRight: '18px', margin: 0, fontSize: '14px', color: '#78350f', lineHeight: 1.8 }}>
            {section.tips.map((tip, i) => (
              <li key={i}>{highlightText(tip, searchQuery)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Field Reference */}
      {section.fields.length > 0 && (
        <div>
          <h3 style={{
            fontSize: '16px', fontWeight: 600, color: '#1f2937',
            marginBottom: '12px',
          }}>
            פירוט שדות
          </h3>
          <div style={{
            border: '1px solid #e5e7eb', borderRadius: '12px',
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: '20%' }}>שדה</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>תיאור</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: '25%' }}>עזרה</th>
                </tr>
              </thead>
              <tbody>
                {section.fields.map((field, i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', fontWeight: 500, color: '#1f2937' }}>
                      {highlightText(field.name, searchQuery)}
                    </td>
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', color: '#374151' }}>
                      {highlightText(field.tooltip, searchQuery)}
                    </td>
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', color: '#6b7280', fontSize: '13px' }}>
                      {field.helper ? highlightText(field.helper, searchQuery) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Navigation Button =====
function NavigationButton({ sections, currentId, direction, onNavigate }: {
  sections: GuideSection[];
  currentId: string;
  direction: 'prev' | 'next';
  onNavigate: (id: string) => void;
}) {
  const currentIndex = sections.findIndex(s => s.id === currentId);
  const targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
  const target = sections[targetIndex];

  if (!target) return <div />;

  return (
    <button
      onClick={() => onNavigate(target.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '8px 14px', borderRadius: '8px',
        border: '1px solid #e5e7eb', backgroundColor: 'white',
        cursor: 'pointer', fontSize: '13px', color: '#374151',
        transition: 'background-color 0.15s',
      }}
    >
      {direction === 'next' && <ChevronLeft size={16} />}
      <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {target.title}
      </span>
      {direction === 'prev' && <ChevronRight size={16} />}
    </button>
  );
}
